import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  deleteAudienceSegment,
  getAudienceSegments,
  IAudienceSegment,
  ResourceEnableDisabled,
  ResourceEnableEnabled,
  updateAudienceSegment,
} from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
import { areFiltersEmpty, ICustomFilterConfig } from '~/src/dash/components/filters/common';
import { FilterRow } from '~/src/dash/components/filters/FilterRow';
import { updateModel } from '~/src/dash/components/model';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { extractSortsFromColumns } from '~/src/dash/components/sorts/common';
import { SortByRow } from '~/src/dash/components/sorts/SortByRow';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractSearchableColumnKeys,
  extractVisibleColumns,
  getComparator,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelectorAndTableExpand } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelectorAndTableExpand';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteAudienceManagerCombineSegments,
  RouteAudienceManagerDataProviderSettings,
  RouteAudienceManagerSegmentExisting,
  RouteAudienceManagerSegmentNew,
} from '~/src/dash/router/routes';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { convertTimestamps } from '~/src/dash/utils/date';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  formatSegmentsResponseIntoLVTRows,
  IAudienceManagerListViewConfig,
} from '~/src/dash/views/segments/audience/AudienceManagerSettingsListView/config';
import { AudienceManagerListContext } from '~/src/dash/views/segments/audience/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IPartnerSettingsButtonProps {
  session: ISessionState;
  user: IUserState;
}

const PartnerSettingsButton = ({ session, user }: IPartnerSettingsButtonProps) => {
  return (
    <ButtonV2
      clearBackground
      allCaps
      onClick={() =>
        navigate(
          RouteAudienceManagerDataProviderSettings,
          extractRouteParams({
            session,
            user,
          }),
        )
      }
      style={{ padding: '5px 16px' }}
    >
      <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
        <Col paddingTop='6px'>
          <img src='/assets/shared/svg/Data_Settings_Icon.svg' />
        </Col>
        <Col>
          <Text small bold color={publicaColors.blue}>
            Partner Settings
          </Text>
        </Col>
      </Cols>
    </ButtonV2>
  );
};

interface IFilter {
  menuTitle: string;
  list: Record<string, IListItem>;
}

type IFiltersState = Record<string, IFilter>;

interface IFilterResponseData {
  props: any;
  filterFn: (item: Record<string, any>, props: any) => boolean;
}

const filterResponseData = (
  responseData: IAudienceSegment[],
  filters: IFiltersState,
  filtersConfig: ICustomFilterConfig,
) => {
  const filteredData = cloneDeep(responseData);
  const filterFunctions: IFilterResponseData[] = [];
  Object.keys(filtersConfig).forEach((filter) => {
    if (filters[filter] && !isEmpty(filters[filter].list)) {
      const { list } = filters[filter];
      const { formatProps, filterFn } = filtersConfig[filter];
      filterFunctions.push({
        props: formatProps ? formatProps(list) : list,
        filterFn,
      });
    }
  });
  return filteredData.filter((item) => {
    return filterFunctions.every((fn) => {
      return fn.filterFn(item, fn.props);
    });
  });
};

const queryFilteredData = (
  filteredData: IAudienceSegment[],
  query: string,
  searchableColumnKeys: string[],
) => {
  let queriedRows = cloneDeep(filteredData);
  if (query !== '') {
    queriedRows = filteredData.filter((row) => {
      for (const key of searchableColumnKeys) {
        // @ts-ignore
        if (row[key].toString().toLowerCase().includes(query.toLowerCase())) {
          return true;
        }
      }
      return false;
    });
  }
  return queriedRows;
};

// reference comments will be removed after the bidders list view is completed

export const AudienceManagerSettingsListView = () => {
  const { classes, location, session, user, isSuperUser } = useBaseStore();
  const { Segments } = useBaseConfig();
  const cfg = Segments.Audience.Main as IAudienceManagerListViewConfig;
  const { cypressClassName, defaultState } = cfg;
  cfg.isInitialized = true;

  // states and refs
  const isMounted = useRef(false);
  const [responseData, setResponseData] = useState<IAudienceSegment[]>([]);
  const [filteredData, setFilteredData] = useState<IAudienceSegment[]>([]);
  const [queriedData, setQueriedData] = useState<IAudienceSegment[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [sortedRows, setSortedRows] = useState([] as IListViewTableRow[]);

  // loading
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);

  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  // search
  const [query, setQuery] = useState('');

  // filters
  const [filters, setFilters] = useState<IFiltersState>({});

  // table states
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    defaultState.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.table.columns),
  );
  const [isTableExpanded, setIsTableExpanded] = useState<boolean>(
    defaultState.isTableExpanded || false,
  );
  const [isTableActionPending, setIsTableActionPending] = useState(false);
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || 'Name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);

  // other consts
  const readAuthorization = IS_AUTHORIZED(['read_audience_manager'], user.user);
  const writeAuthorization = IS_AUTHORIZED(['write_audience_manager'], user.user);
  const { pathname } = location;
  const searchableColumnKeys = extractSearchableColumnKeys(cfg.table.columns);
  const sorts = extractSortsFromColumns(cfg.sorts, cfg.table.columns);

  // call api on load
  useEffect(() => {
    getData();
  }, []);

  // filter responseData via pills
  useEffect(() => {
    if (isMounted.current) {
      setFilteredData(filterResponseData(responseData, filters, cfg.filters));
    }
  }, [responseData, filters]);

  // filter filteredData via search query
  useEffect(() => {
    if (isMounted.current) {
      const queriedRows = queryFilteredData(filteredData, query, searchableColumnKeys);
      unstable_batchedUpdates(() => {
        setQueriedData(queriedRows);
        setTotalRowCount(queriedRows.length);
        setPage(0);
        setLoadState(!isEmpty(queriedRows) ? LoadingStateHasData : LoadingStateNoData);
      });
    }
  }, [filteredData, query]);

  // sort queriedData via column or sort bys
  useEffect(() => {
    if (isMounted.current) {
      if (!isEmpty(queriedData)) {
        setSortedRows(
          stableSort(rows, getComparator(order, orderBy, cfg.table.columns[orderBy].valueType)),
        );
      } else {
        setSortedRows([]);
      }
    }
  }, [queriedData, order, orderBy]);

  // update config on selected columns change
  useEffect(() => {
    if (isMounted.current) {
      // @ts-ignore
      cfg.defaultState.activeColumnKeys = activeColumnKeys;
    }
  }, [activeColumnKeys]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, []);

  // main query
  const getData = () => {
    if (readAuthorization.isAuthorized) {
      setLoadState(LoadingStateLoading);
      getAudienceSegments({
        query: {
          publisher_id: session.publisher.ID,
        },
      })
        .then((data: IAudienceSegment[]) => {
          // make datekey dynamic
          const convertedData = convertTimestamps(
            data,
            ['CreatedAt', 'UpdatedAt'],
            session.publisher.Timezone,
          );
          unstable_batchedUpdates(() => {
            setResponseData(convertedData as IAudienceSegment[]);
          });
        })
        .catch(() => {
          unstable_batchedUpdates(() => {
            setResponseData([]);
            setLoadState(LoadingStateNoData);
            setSnackbarMessage('Error retrieving data.');
            setSnackbarError(true);
          });
        });
    }
  };

  const findSegment = (id: string) => {
    return responseData.reduce((ret: IAudienceSegment | undefined, r: IAudienceSegment) => {
      if (r.ID === id) {
        return r;
      }
      return ret;
    }, undefined);
  };

  const updateSegment = (segment: IAudienceSegment) => {
    const { ID, Name } = segment;
    setIsTableActionPending(true);
    updateAudienceSegment({
      body: {
        publisher_id: session.publisher.ID,
        segment_id: segment.ID,
        segment,
      },
    })
      .then((segment: IAudienceSegment) => {
        unstable_batchedUpdates(() => {
          setResponseData(updateModel(responseData, segment));
          setIsTableActionPending(false);
          setSnackbarMessage(
            `[${ID}] ${Name} is now ${
              segment.Active === ResourceEnableEnabled ? 'active' : 'inactive'
            }.`,
          );
          setSnackbarError(false);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(`Failed to update segment [${ID}] ${Name}.`);
          setSnackbarError(true);
        });
      });
  };

  // begin handles below
  // table handles
  const handleOnChangeStatus = (id: string, checked: boolean) => {
    const segment = findSegment(id);
    if (segment) {
      segment.Active = checked ? ResourceEnableEnabled : ResourceEnableDisabled;
      updateSegment(segment);
    }
  };

  const handleDeleteItem = (id: string) => {
    setIsTableActionPending(true);
    deleteAudienceSegment({
      query: {
        publisher_id: session.publisher.ID,
        segment_id: id,
      },
    })
      .then(() => {
        const updatedData = responseData.filter((segment: IAudienceSegment) => segment.ID !== id);
        unstable_batchedUpdates(() => {
          setResponseData(updatedData);
          setSnackbarMessage('Successfully deleted segment');
          setSnackbarError(false);
          setIsTableActionPending(false);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setSnackbarMessage('Error while deleting segment.');
          setSnackbarError(true);
          setIsTableActionPending(false);
        });
      });
    // the delete modal expects a promise to be returned
    return new Promise((resolve) => resolve(undefined));
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { key, defaultOrderDirection } = column;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    unstable_batchedUpdates(() => {
      setRowsPerPage(parseInt(event.target.value, 10) as RowsPerPage);
      setPage(0);
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  const handleOnChangeQuery = (text: string) => {
    unstable_batchedUpdates(() => {
      setLoadState(LoadingStateLoading);
      setQuery(text);
    });
    track('Audience Segments - Search', { isSuperUser }, user.user);
  };

  // filter functions
  const handleFilterAccept = (menuTitle: string, list: IMenuList) => {
    if (!filters[menuTitle] && isEmpty(list)) {
      cfg.filters[menuTitle].defaultSelected = [];
      return;
    }
    setFilters((preFilters: any) => {
      const clonedFilters = cloneDeep(preFilters);
      clonedFilters[menuTitle] = {
        menuTitle,
        list,
      };
      cfg.filters[menuTitle].defaultSelected = Object.values(list).map((item) => item.ID);
      return clonedFilters;
    });
    track('Audience Segments - Use Filter', { isSuperUser, filter: menuTitle }, user.user);
  };

  const handleClearFilters = () => {
    unstable_batchedUpdates(() => {
      setFilters({} as IFiltersState);
    });
  };

  const handleSelectSortBy = (orderByKey: string) => {
    unstable_batchedUpdates(() => {
      setOrder(cfg.table.columns[orderByKey].defaultOrderDirection || 'asc');
      setOrderBy(orderByKey);
      setPage(0);
    });
  };

  // top buttons
  const handleOnClickSegmentButtons = (type: 'create' | 'combine') => {
    if (responseData.length >= 300) {
      unstable_batchedUpdates(() => {
        setSnackbarError(true),
          setSnackbarMessage(
            'You have reached your maximum limit of 300 segments. Please delete some of your unused segments in order to create new ones.',
          );
      });
    } else {
      let route;
      switch (type) {
        case 'create':
          route = RouteAudienceManagerSegmentNew;
          track('Audience Segments - Add New', { isSuperUser }, user.user);
          break;
        case 'combine':
          route = RouteAudienceManagerCombineSegments;
        default:
          break;
      }
      if (route) {
        navigate(route, extractRouteParams({ session, user }));
      }
    }
  };

  // active columns
  const handleChangeActiveColumns = (keys: string[]) => {
    setActiveColumnKeys(keys);
  };

  // end handles above

  const getAudienceSegmentSettingsRoute = (id: string): string => {
    return getRoutePath(
      RouteAudienceManagerSegmentExisting,
      extractRouteParams({ session, user }),
      {
        audienceID: id,
      },
    );
  };

  // convert the data to use the new table interface
  const rows = formatSegmentsResponseIntoLVTRows(queriedData, cfg.table);

  // since all data is collected, we handle the pagination here
  // this only works because all rows have been pulled
  // a different method will be needed if pagination is handled via api
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  // update config
  cfg.defaultState.orderBy = orderBy;
  cfg.defaultState.order = order;
  (cfg.defaultState.page = page), (cfg.defaultState.rowsPerPage = rowsPerPage);

  return (
    <AudienceManagerListContext.Provider value={{ cfg }}>
      <Panels max vertical relative minWidthMain>
        <Panel
          largeHorizontalPadding
          verticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          <ModelTopBar
            classes={classes}
            currentPath={pathname}
            disableTitlePaddingLeft
            publisher={session.publisher}
            session={session}
            title={routeName(pathname)}
            user={user}
          />
        </Panel>
        <Panel scroll paddingTop={spacing.small}>
          <Panels
            max
            vertical
            relative
            verticalPadding
            largeHorizontalPadding
            style={{ rowGap: spacing.medium }}
          >
            {/* TOP BUTTONS */}
            <Panel>
              <Cols
                wrap
                gap={spacing.small}
                style={{
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  rowGap: spacing.small,
                }}
              >
                {/* PARTNER SETTINGS BUTTON */}
                {hasRoleSuperUser(user.user) && (
                  <Col>
                    <PartnerSettingsButton session={session} user={user} />
                  </Col>
                )}
                <Col>
                  <Cols
                    wrap
                    gap={spacing.small}
                    style={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      rowGap: spacing.extraSmall,
                    }}
                  >
                    {/* CREATE SEGMENT BUTTON */}
                    <Col>
                      <ButtonV2
                        onClick={() => handleOnClickSegmentButtons('create')}
                        className={`cypress-${cypressClassName}-button-createsegment`}
                      >
                        Create Segment
                      </ButtonV2>
                    </Col>
                    {/* COMBINE SEGMENTS BUTTON */}
                    {session.publisher.LiverampEnabled === EBool.EBool_TRUE &&
                      (session.publisher.LiverampDataStore !== '' ||
                        session.publisher.LiverampOTTHubCustomerID !== '') && (
                        <Col>
                          <ButtonV2 onClick={() => handleOnClickSegmentButtons('combine')}>
                            Combine Segments
                          </ButtonV2>
                        </Col>
                      )}
                  </Cols>
                </Col>
              </Cols>
            </Panel>
            <Panel highlighted>
              <Rows>
                <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
                  <FilterRow
                    cypressParentName={cypressClassName}
                    filtersConfig={cfg.filters}
                    handleFilterAccept={handleFilterAccept}
                    handleClearFilters={handleClearFilters}
                    enableClear={!areFiltersEmpty(filters)}
                  />
                </Row>
                <Row noPadding>
                  <SortByRow
                    sorts={sorts}
                    currentOrder={order}
                    currentOrderBy={orderBy}
                    handleSelectSortBy={handleSelectSortBy}
                  />
                </Row>
              </Rows>
            </Panel>
            <Panel highlighted largeHorizontalPadding verticalPadding>
              <Rows>
                <Row style={{ padding: '8px 0px 16px' }}>
                  <Cols
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Col>
                      <TextInput
                        enableClear
                        handleOnChange={handleOnChangeQuery}
                        msDelay={300}
                        placeholder='Search by Name'
                        prefix='search'
                      />
                    </Col>
                    <Col>
                      <ColumnSelectorAndTableExpand
                        // simple multi select props
                        preselectedColumnKeys={cfg.defaultState.activeColumnKeys}
                        resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                        columnsList={extractVisibleColumns(
                          cfg.table.columns,
                          activeColumnKeys || [],
                        )}
                        columnsHandleOnChangeOption={handleChangeActiveColumns}
                        columnsHandleOnAccept={handleChangeActiveColumns}
                        // icon toggle props
                        isTableExpanded={isTableExpanded}
                        setIsTableExpanded={setIsTableExpanded}
                      />
                    </Col>
                  </Cols>
                </Row>
                <Row noPadding>
                  <ListViewTable
                    activeColumnKeys={activeColumnKeys}
                    columns={cfg.table.columns}
                    isExpanded={isTableExpanded}
                    enableActions
                    enableStatusSwitch
                    getItemSettingsRoute={getAudienceSegmentSettingsRoute}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    handleDeleteItem={handleDeleteItem}
                    handleOnChangeStatus={handleOnChangeStatus}
                    handleOnClickColumnHeader={handleOnClickColumnHeader}
                    isActionPending={isTableActionPending}
                    isReadAuthorized={readAuthorization.isAuthorized}
                    isWriteAuthorized={writeAuthorization.isAuthorized}
                    loadState={loadState}
                    order={order}
                    orderBy={orderBy}
                    page={page}
                    rows={visibleRows}
                    rowsPerPage={rowsPerPage}
                    totalRowCount={totalRowCount}
                    className={cypressClassName}
                  />
                </Row>
              </Rows>
            </Panel>
            {/* snackbar is a special case and can act as a panel */}
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={handleSnackbarClose}
            >
              {snackbarMessage}
            </SnackbarView>
            <PanelBreak height={spacing.large} />
          </Panels>
        </Panel>
      </Panels>
    </AudienceManagerListContext.Provider>
  );
};
